/**
 * HTTP Slack client — talks to Zunelo `/api/slack/*` (Functions or local BFF).
 * Secrets never ship in the SPA bundle; auth is a Firebase
 * ID token sent as `Authorization: Bearer …`.
 */

import { ApiEnvelope, SlackThread } from '../../domain/types';
import {
  ISlackClient,
  ProviderConnectionProbe,
  SlackConnectionAck,
  HttpClientOptions,
} from './types';
import { createHttpTransport, type HttpTransport } from './httpTransport';
import { parseEnvelope } from './parseEnvelope';
import type { UserSettingsPublic } from '@zunelo/shared';

export class HttpSlackClient implements ISlackClient {
  private readonly transport: HttpTransport;

  constructor(options: HttpClientOptions = {}) {
    this.transport = createHttpTransport(options);
  }

  async getThreads(): Promise<ApiEnvelope<SlackThread[]>> {
    const response = await this.transport.fetch('/api/slack/threads');
    return parseEnvelope<SlackThread[]>(response, undefined, 'SLACK_HTTP_ERROR');
  }

  async refresh(
    hours = 24,
  ): Promise<ApiEnvelope<{ refreshed: boolean; hours: number; count: number }>> {
    const response = await this.transport.fetch(`/api/slack/refresh?hours=${hours}`, {
      method: 'POST',
    });
    return parseEnvelope<{ refreshed: boolean; hours: number; count: number }>(
      response,
      undefined,
      'SLACK_HTTP_ERROR',
    );
  }

  async getThread(threadTs: string): Promise<ApiEnvelope<SlackThread | null>> {
    const response = await this.transport.fetch(
      `/api/slack/threads/${encodeURIComponent(threadTs)}`,
    );
    return parseEnvelope<SlackThread | null>(response, undefined, 'SLACK_HTTP_ERROR');
  }

  async getConversations(_options?: { fresh?: boolean }): Promise<ApiEnvelope<never[]>> {
    // Retired in v1.4.19: unread conversations were replaced by the
    // cross-source "Asuntos sin atender" inbox, which lives on its own
    // client. Returning an empty envelope keeps the contract callable for
    // any caller still wired up before the cutover.
    return { data: [], serverTime: new Date().toISOString() };
  }

  async postReply(
    channelName: string,
    threadTs: string,
    text: string,
    idempotencyKey?: string,
  ): Promise<ApiEnvelope<SlackThread>> {
    const key =
      idempotencyKey ??
      (typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `reply-${Date.now()}`);
    const response = await this.transport.fetch(
      `/api/slack/threads/${encodeURIComponent(threadTs)}/replies`,
      {
        method: 'POST',
        body: JSON.stringify({ channelName, text, idempotencyKey: key }),
      },
    );
    return parseEnvelope<SlackThread>(response, undefined, 'SLACK_HTTP_ERROR');
  }

  async generateAiSummary(
    channelName: string,
    threadTs: string,
    options?: { force?: boolean; mode?: 'auto' | 'incremental' | 'full' },
  ): Promise<
    ApiEnvelope<{ summary: string; actionItem: string; refreshed?: boolean; mode?: string }>
  > {
    const response = await this.transport.fetch(
      `/api/slack/threads/${encodeURIComponent(threadTs)}/summary`,
      {
        method: 'POST',
        body: JSON.stringify({
          channelName,
          ...(options?.force === true ? { force: true } : {}),
          ...(options?.mode ? { mode: options.mode } : {}),
        }),
      },
    );
    return parseEnvelope<{
      summary: string;
      actionItem: string;
      refreshed?: boolean;
      mode?: string;
    }>(response, undefined, 'SLACK_HTTP_ERROR');
  }

  async generateReplyDraft(
    channelName: string,
    threadTs: string,
    hint?: string,
  ): Promise<ApiEnvelope<{ draft: string; provider?: string; model?: string }>> {
    const response = await this.transport.fetch(
      `/api/slack/threads/${encodeURIComponent(threadTs)}/draft`,
      {
        method: 'POST',
        body: JSON.stringify({
          channelName,
          ...(hint?.trim() ? { hint: hint.trim() } : {}),
        }),
      },
    );
    return parseEnvelope<{ draft: string; provider?: string; model?: string }>(
      response,
      undefined,
      'SLACK_HTTP_ERROR',
    );
  }

  async askAboutThread(
    channelName: string,
    threadTs: string,
    question: string,
  ): Promise<ApiEnvelope<{ answer: string; provider?: string; model?: string }>> {
    const response = await this.transport.fetch(
      `/api/slack/threads/${encodeURIComponent(threadTs)}/ask`,
      {
        method: 'POST',
        body: JSON.stringify({
          channelName,
          question: question.trim(),
        }),
      },
    );
    return parseEnvelope<{ answer: string; provider?: string; model?: string }>(
      response,
      undefined,
      'SLACK_HTTP_ERROR',
    );
  }

  async getConnection(): Promise<ApiEnvelope<SlackConnectionAck>> {
    const response = await this.transport.fetch('/api/slack/connection');
    return parseEnvelope<SlackConnectionAck>(response, undefined, 'SLACK_HTTP_ERROR');
  }

  async testConnection(): Promise<ApiEnvelope<ProviderConnectionProbe>> {
    const response = await this.transport.fetch('/api/slack/connection/test', { method: 'POST' });
    return parseEnvelope<ProviderConnectionProbe>(response);
  }
  async disconnectOAuth(): Promise<ApiEnvelope<UserSettingsPublic>> {
    const response = await this.transport.fetch('/api/slack/oauth/disconnect', { method: 'POST' });
    return parseEnvelope<UserSettingsPublic>(response);
  }
}
